/**
 * GlobalFilters.jsx - Componente de Filtros Globais Reutilizáveis
 * CG Business ERP - P2
 * 
 * Filtros disponíveis:
 * - period: Período temporal
 * - location: Localização
 * - employee: Funcionário
 * - status: Estado (dinâmico por módulo)
 * - category: Categoria/Tipo
 * - payment_account: Forma de Pagamento/Conta
 * - deleted: Activos/Eliminados/Todos
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { settingsAPI, catalogsAPI } from '@/lib/api';
import {
  Calendar, MapPin, User, Filter, RefreshCw, Trash2, X,
  CreditCard, Tag, ChevronDown
} from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n';

// =============================================================================
// PERIOD OPTIONS
// =============================================================================
const PERIOD_OPTION_KEYS = [
  { value: 'all', key: 'allTime' },
  { value: 'today', key: 'today' },
  { value: 'yesterday', key: 'yesterday' },
  { value: 'this_week', key: 'thisWeek' },
  { value: 'this_month', key: 'thisMonth' },
  { value: 'last_month', key: 'lastMonth' },
  { value: 'this_year', key: 'thisYear' },
  { value: 'custom', key: 'custom' },
];

// =============================================================================
// DELETED OPTIONS
// =============================================================================
const DELETED_OPTION_KEYS = [
  { value: 'active', key: 'active' },
  { value: 'deleted', key: 'deleted' },
  { value: 'all', key: 'all' },
];

// =============================================================================
// DEFAULT VALUES
// =============================================================================
const DEFAULT_VALUES = {
  period: 'all',
  location_id: 'all',
  employee_id: 'all',
  status: 'all',
  category: 'all',
  payment_account_id: 'all',
  deleted: 'active',
  custom_start: null,
  custom_end: null,
};

const FILTER_KEYS_WITH_ALL_SENTINEL = ['location_id', 'employee_id', 'status', 'category', 'payment_account_id'];

const normalizeFilterPayload = (rawValues = {}) => {
  const serializeDateValue = (value) => {
    if (!value) return null;
    return value instanceof Date ? value.toISOString() : value;
  };

  const payload = {
    period: rawValues.period || 'all',
    deleted: rawValues.deleted || 'active',
    custom_start: rawValues.period === 'custom' ? serializeDateValue(rawValues.custom_start) : null,
    custom_end: rawValues.period === 'custom' ? serializeDateValue(rawValues.custom_end) : null,
  };

  FILTER_KEYS_WITH_ALL_SENTINEL.forEach((key) => {
    payload[key] = rawValues[key] && rawValues[key] !== 'all' ? rawValues[key] : null;
  });

  return payload;
};

// =============================================================================
// GLOBAL FILTERS COMPONENT
// =============================================================================
export default function GlobalFilters({
  // Which filters to show
  filters = ['period', 'location'],
  
  // Status options (dynamic per module)
  statusOptions = [],
  statusLabels = {},
  
  // Category options (dynamic per module)  
  categoryOptions = [],
  categoryLabels = {},
  
  // Payment account options (fetched if not provided)
  paymentAccountOptions = null,
  
  // Callback when filters change
  onChange,
  
  // Default values
  defaultPeriod = 'all',
  defaultLocation = 'all',
  defaultStatus = 'all',
  defaultDeleted = 'active',
  
  // Show refresh button
  showRefresh = true,
  onRefresh,
  
  // Loading state
  loading = false,
  
  // Compact mode
  compact = false,

  // Visual customization
  filterTriggerClassName = '',
  actionButtonClassName = '',
  activeBadgeClassName = '',
  
  // Additional CSS classes
  className = '',
}) {
  const { t } = useLanguage();
  // ==========================================================================
  // STATE
  // ==========================================================================
  const [values, setValues] = useState({
    ...DEFAULT_VALUES,
    period: defaultPeriod,
    location_id: defaultLocation,
    status: defaultStatus,
    deleted: defaultDeleted,
  });
  
  // Data from API
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Custom date range popover
  const [customDateOpen, setCustomDateOpen] = useState(false);

  useEffect(() => {
    setValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        period: defaultPeriod,
        location_id: defaultLocation,
        status: defaultStatus,
        deleted: defaultDeleted,
      };

      if (defaultPeriod !== 'custom') {
        nextValues.custom_start = null;
        nextValues.custom_end = null;
      }

      const hasChanged = (
        currentValues.period !== nextValues.period
        || currentValues.location_id !== nextValues.location_id
        || currentValues.status !== nextValues.status
        || currentValues.deleted !== nextValues.deleted
        || currentValues.custom_start !== nextValues.custom_start
        || currentValues.custom_end !== nextValues.custom_end
      );

      return hasChanged ? nextValues : currentValues;
    });
  }, [defaultDeleted, defaultLocation, defaultPeriod, defaultStatus]);
  
  // ==========================================================================
  // LOAD DATA
  // ==========================================================================
  const loadFilterData = useCallback(async () => {
    try {
      const promises = [];
      
      // Load locations if needed
      if (filters.includes('location')) {
        promises.push(
          settingsAPI.listActiveLocations()
            .then(res => setLocations(res.data || []))
            .catch(() => setLocations([]))
        );
      }
      
      // Load employees if needed
      if (filters.includes('employee')) {
        promises.push(
          settingsAPI.listActiveEmployees({ compact: true })
            .then(res => setEmployees(res.data || []))
            .catch(() => setEmployees([]))
        );
      }
      
      // Load payment accounts if needed and not provided
      if (filters.includes('payment_account') && !paymentAccountOptions) {
        promises.push(
          settingsAPI.listActivePaymentAccounts()
            .then(res => setPaymentAccounts(res.data || []))
            .catch(() => setPaymentAccounts([]))
        );
      }
      
      // Load categories if needed and not provided
      if (filters.includes('category') && categoryOptions.length === 0) {
        promises.push(
          catalogsAPI.listProductCategories()
            .then(res => setCategories(res.data || []))
            .catch(() => setCategories([]))
        );
      }
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  }, [filters, paymentAccountOptions, categoryOptions.length]);
  
  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);
  
  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleChange = useCallback((key, value) => {
    const newValues = { ...values, [key]: value };

    if (key === 'period' && value !== 'custom') {
      newValues.custom_start = null;
      newValues.custom_end = null;
    }

    setValues(newValues);

    if (key === 'period' && value === 'custom') {
      setCustomDateOpen(true);
    }

    if (onChange) {
      onChange(normalizeFilterPayload(newValues));
    }
  }, [onChange, values]);
  
  const handleClearAll = useCallback(() => {
    const resetValues = {
      ...DEFAULT_VALUES,
      period: defaultPeriod,
      location_id: defaultLocation,
      status: defaultStatus,
      deleted: defaultDeleted,
    };
    setValues(resetValues);
    if (onChange) {
      onChange(normalizeFilterPayload(resetValues));
    }
  }, [onChange, defaultPeriod, defaultLocation, defaultStatus, defaultDeleted]);
  
  const handleDateRangeSelect = useCallback((start, end) => {
    const nextValues = {
      ...values,
      period: 'custom',
      custom_start: start,
      custom_end: end,
    };
    setValues(nextValues);
    setCustomDateOpen(false);
    if (onChange) {
      onChange(normalizeFilterPayload(nextValues));
    }
  }, [onChange, values]);
  
  // ==========================================================================
  // COMPUTED
  // ==========================================================================
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (values.period !== defaultPeriod) count++;
    if (values.location_id !== 'all') count++;
    if (values.employee_id !== 'all') count++;
    if (values.status !== 'all') count++;
    if (values.category !== 'all') count++;
    if (values.payment_account_id !== 'all') count++;
    if (values.deleted !== 'active') count++;
    return count;
  }, [values, defaultPeriod]);
  
  // Use provided payment accounts or fetched ones
  const finalPaymentAccounts = paymentAccountOptions || paymentAccounts;
  
  // Use provided categories or fetched ones
  const finalCategories = categoryOptions.length > 0 ? categoryOptions : categories;
  const periodOptions = useMemo(() => PERIOD_OPTION_KEYS.map((option) => ({ ...option, label: t(`globalFilters.periods.${option.key}`) })), [t]);
  const deletedOptions = useMemo(() => DELETED_OPTION_KEYS.map((option) => ({ ...option, label: t(`globalFilters.deleted.${option.key}`) })), [t]);
  
  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div className={cn(
      "flex flex-wrap items-center gap-2",
      compact ? "gap-1.5" : "gap-2",
      className
    )} data-testid="global-filters">
      
      {/* Period Filter */}
      {filters.includes('period') && (
        <div className="flex items-center gap-1">
          <Select value={values.period} onValueChange={(v) => handleChange('period', v)}>
            <SelectTrigger 
              className={cn("w-36", compact && "h-8 text-xs", filterTriggerClassName)}
              data-testid="filter-period"
            >
              <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Custom Date Range Popover */}
          {values.period === 'custom' && (
            <Popover open={customDateOpen} onOpenChange={setCustomDateOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  size={compact ? "sm" : "default"}
                  className={cn("gap-1", compact && "h-8 text-xs", actionButtonClassName)}
                >
                  {values.custom_start && values.custom_end ? (
                    `${format(values.custom_start, 'dd/MM')} - ${format(values.custom_end, 'dd/MM')}`
                  ) : (
                    t('globalFilters.selectDates')
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('globalFilters.selectPeriod')}</p>
                  <div className="flex gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('globalFilters.from')}</p>
                      <Input
                        type="date"
                        value={values.custom_start ? format(values.custom_start, 'yyyy-MM-dd') : ''}
                        onChange={(e) => setValues(prev => ({
                          ...prev,
                          custom_start: e.target.value ? new Date(e.target.value) : null
                        }))}
                        className="w-36"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('globalFilters.to')}</p>
                      <Input
                        type="date"
                        value={values.custom_end ? format(values.custom_end, 'yyyy-MM-dd') : ''}
                        onChange={(e) => setValues(prev => ({
                          ...prev,
                          custom_end: e.target.value ? new Date(e.target.value) : null
                        }))}
                        className="w-36"
                      />
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    className="w-full"
                    onClick={() => handleDateRangeSelect(values.custom_start, values.custom_end)}
                    disabled={!values.custom_start || !values.custom_end}
                  >
                    {t('globalFilters.apply')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
      
      {/* Location Filter */}
      {filters.includes('location') && (
        <Select value={values.location_id} onValueChange={(v) => handleChange('location_id', v)}>
          <SelectTrigger 
            className={cn("w-44", compact && "h-8 text-xs", filterTriggerClassName)}
            data-testid="filter-location"
          >
            <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder={t('globalFilters.location')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('globalFilters.allLocations')}</SelectItem>
            {locations.map(loc => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.type === 'store' ? 'Loja' : loc.type === 'warehouse' ? 'Armazém' : 'Local'} · {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {/* Employee Filter */}
      {filters.includes('employee') && (
        <Select value={values.employee_id} onValueChange={(v) => handleChange('employee_id', v)}>
          <SelectTrigger 
            className={cn("w-40", compact && "h-8 text-xs", filterTriggerClassName)}
            data-testid="filter-employee"
          >
            <User className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder={t('globalFilters.employee')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('globalFilters.allEmployees')}</SelectItem>
            {employees.map(emp => (
              <SelectItem key={emp.id} value={emp.id}>
                {emp.employee_number ? `${emp.employee_number} · ${emp.name}` : emp.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {/* Status Filter */}
      {filters.includes('status') && statusOptions.length > 0 && (
        <Select value={values.status} onValueChange={(v) => handleChange('status', v)}>
          <SelectTrigger 
            className={cn("w-36", compact && "h-8 text-xs", filterTriggerClassName)}
            data-testid="filter-status"
          >
            <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder={t('globalFilters.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('globalFilters.allStatuses')}</SelectItem>
            {statusOptions.map(status => (
              <SelectItem key={status} value={status}>
                {statusLabels[status] || status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {/* Category Filter */}
      {filters.includes('category') && finalCategories.length > 0 && (
        <Select value={values.category} onValueChange={(v) => handleChange('category', v)}>
          <SelectTrigger 
            className={cn("w-40", compact && "h-8 text-xs", filterTriggerClassName)}
            data-testid="filter-category"
          >
            <Tag className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder={t('globalFilters.category')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('globalFilters.allCategories')}</SelectItem>
            {finalCategories.map(cat => (
              <SelectItem key={cat.id || cat.key || cat} value={cat.id || cat.key || cat}>
                {cat.name || cat.display_name || categoryLabels[cat] || cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {/* Payment Account Filter */}
      {filters.includes('payment_account') && finalPaymentAccounts.length > 0 && (
        <Select value={values.payment_account_id} onValueChange={(v) => handleChange('payment_account_id', v)}>
          <SelectTrigger 
            className={cn("w-40", compact && "h-8 text-xs", filterTriggerClassName)}
            data-testid="filter-payment-account"
          >
            <CreditCard className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder={t('globalFilters.account')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('globalFilters.allAccounts')}</SelectItem>
            {finalPaymentAccounts.map(acc => (
              <SelectItem key={acc.id} value={acc.id}>
                {acc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {/* Deleted Filter */}
      {filters.includes('deleted') && (
        <Select value={values.deleted} onValueChange={(v) => handleChange('deleted', v)}>
          <SelectTrigger 
            className={cn("w-32", compact && "h-8 text-xs", filterTriggerClassName)}
            data-testid="filter-deleted"
          >
            <Trash2 className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {deletedOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {/* Active Filters Badge + Clear */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-1">
          <Badge variant="secondary" className={cn("text-xs", activeBadgeClassName)}>
            {t('globalFilters.activeFilters', { count: activeFiltersCount })}
          </Badge>
          <Button
            variant="ghost"
            size={compact ? "sm" : "icon"}
            onClick={handleClearAll}
            className={cn("h-8 w-8", compact && "h-7 w-7", actionButtonClassName)}
            title={t('globalFilters.clearFilters')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {/* Refresh Button */}
      {showRefresh && onRefresh && (
        <Button
          variant="outline"
          size={compact ? "sm" : "icon"}
          onClick={onRefresh}
          disabled={loading}
          className={cn("h-9 w-9", compact && "h-8 w-8", actionButtonClassName)}
          data-testid="filter-refresh"
        >
          <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
        </Button>
      )}
    </div>
  );
}

// =============================================================================
// HOOK: useGlobalFilters
// =============================================================================
export function useGlobalFilters(initialFilters = {}) {
  const [filters, setFilters] = useState({
    period: 'all',
    location_id: null,
    employee_id: null,
    status: null,
    category: null,
    payment_account_id: null,
    deleted: 'active',
    custom_start: null,
    custom_end: null,
    ...initialFilters,
  });
  
  const handleFilterChange = useCallback((newFilters) => {
    const normalizedFilters = { ...newFilters };

    FILTER_KEYS_WITH_ALL_SENTINEL.forEach((key) => {
      if (normalizedFilters[key] === 'all') {
        normalizedFilters[key] = null;
      }
    });

    setFilters((prev) => {
      const next = { ...prev, ...normalizedFilters };

      if (Object.prototype.hasOwnProperty.call(normalizedFilters, 'period') && normalizedFilters.period !== 'custom') {
        next.custom_start = normalizedFilters.custom_start ?? null;
        next.custom_end = normalizedFilters.custom_end ?? null;
      }

      return next;
    });
  }, []);
  
  const clearFilters = useCallback(() => {
    setFilters({
      period: 'all',
      location_id: null,
      employee_id: null,
      status: null,
      category: null,
      payment_account_id: null,
      deleted: 'active',
      custom_start: null,
      custom_end: null,
    });
  }, []);
  
  // Build query params for API calls
  const queryParams = useMemo(() => {
    const params = {};
    if (filters.period && filters.period !== 'all') params.period = filters.period;
    if (filters.location_id) params.location_id = filters.location_id;
    if (filters.employee_id) params.employee_id = filters.employee_id;
    if (filters.status) params.status = filters.status;
    if (filters.category) params.category = filters.category;
    if (filters.payment_account_id) params.payment_account_id = filters.payment_account_id;
    if (filters.deleted !== 'active') {
      params.include_deleted = filters.deleted === 'all';
      params.deleted_only = filters.deleted === 'deleted';
    }
    if (filters.custom_start) params.start_date = filters.custom_start;
    if (filters.custom_end) params.end_date = filters.custom_end;
    return params;
  }, [filters]);
  
  return {
    filters,
    setFilters: handleFilterChange,
    clearFilters,
    queryParams,
  };
}
